import { useEffect, useState } from 'react'
import { QueryClient, QueryClientProvider } from '@tanstack/react-query'
import { RouterProvider } from '@tanstack/react-router'
import { Loader2 } from 'lucide-react'
import { router } from '@/router'
import { ready, getStartParam } from '@/lib/telegram'
import { useAppStore } from '@/lib/store'
import { checkAuth, markLoggedOut, useAuthStore } from '@/lib/auth'
import { api } from '@/lib/api'
import { applyBrandTheme } from '@/lib/brandTheme'
import { isLodgingShop } from '@/lib/format'
import { ConsentBanner } from '@/components/ConsentBanner'
import { ServerStatusBanner } from '@/components/ServerStatusBanner'
import { ApiError } from '@/lib/api'

import { initAnalytics } from '@/lib/analytics'

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000,
      retry: (failureCount, error) => {
        // Automatically retry during server restart or deployment (502, 503, 504, network drops)
        if (error instanceof ApiError && [502, 503, 504, 0].includes(error.status)) {
          return failureCount < 6
        }
        return failureCount < 1
      },
      retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 8000),
    },
  },
})

// Routes a browser visitor can view without any session — the public
// storefront (catalog, product pages, quiz, static content pages).
// Everything else (cart, checkout, profile, orders, ...) needs an
// identity, so an unauthenticated visit there still bounces to /login.
// Inside Telegram this never matters — checkAuth() short-circuits to
// authenticated before any of this runs.
const PUBLIC_PATH_PREFIXES = [
  '/catalog',
  '/product',
  '/quiz',
  '/scent-finder',
  '/compass',
  '/brands',
  '/brand',
  '/specs',
  '/pages',
]

function isPublicPath(pathname: string): boolean {
  if (pathname === '/') return true
  return PUBLIC_PATH_PREFIXES.some((p) => pathname === p || pathname.startsWith(`${p}/`))
}

function Bootstrap() {
  const setLang = useAppStore((s) => s.setLang)
  const authStatus = useAuthStore((s) => s.status)
  const [ready_, setReady] = useState(false)
  const [ga4MeasurementId, setGa4MeasurementId] = useState<string | undefined>(undefined)

  useEffect(() => {
    ready()
    const startParam = getStartParam()

    api
      .get<{
        brand_primary_color: string | null
        ga4_measurement_id?: string
        google_analytics_id?: string
        meta_pixel_id?: string
        shop_name?: string
        supported_languages?: string[]
      }>('/settings')
      .catch(() => undefined)
      .then(async (settings) => {
        if (settings) {
          applyBrandTheme(settings.brand_primary_color)
          const gaId = settings.google_analytics_id || settings.ga4_measurement_id
          setGa4MeasurementId(gaId || undefined)
          initAnalytics({
            google_analytics_id: gaId,
            meta_pixel_id: settings.meta_pixel_id,
          })
        }
        // Lodging shops default to Russian, but an authenticated user's
        // saved profile language must win once it is available.
        const isLodging = isLodgingShop(settings?.shop_name)
        const shopDefaultLang: 'uk' | 'ru' = isLodging ? 'ru' : 'uk'
        const supportedLanguages = settings?.supported_languages ?? (isLodging ? ['ru', 'en'] : ['uk', 'en', 'ru'])
        const firstSupportedLang = supportedLanguages.find(
          (language): language is 'uk' | 'en' | 'ru' =>
            language === 'uk' || language === 'en' || language === 'ru',
        )
        const fallbackLang = supportedLanguages.includes(shopDefaultLang)
          ? shopDefaultLang
          : (firstSupportedLang ?? shopDefaultLang)
        const normalizeLang = (language: string): 'uk' | 'en' | 'ru' => {
          if (
            (language === 'uk' || language === 'en' || language === 'ru') &&
            supportedLanguages.includes(language)
          ) {
            return language
          }
          return fallbackLang
        }
        setLang(normalizeLang(shopDefaultLang))

        const ok = await checkAuth()
        if (!ok) {
          setLang(normalizeLang(shopDefaultLang))
          // Public storefront pages stay visible to anonymous browser
          // visitors; only gated pages force the login redirect.
          if (!isPublicPath(router.state.location.pathname)) {
            void router.navigate({ to: '/login' })
          }
        } else {
          // Authenticated: respect the saved profile language when it
          // exists, otherwise fall back to the shop default.
          try {
            const session = await api.get<{ provider?: string }>('/auth/me')
            if (session.provider && session.provider !== 'guest') {
              const profile = await api.get<{ language: string }>('/me')
              setLang(normalizeLang(profile.language))
            } else {
              setLang(normalizeLang(shopDefaultLang))
            }
          } catch {
            setLang(normalizeLang(shopDefaultLang))
          }
          if (startParam.startsWith('p')) {
            void router.navigate({
              to: '/catalog/$productSlug',
              params: { productSlug: startParam.slice(1) },
            })
          }
        }
        setReady(true)
      })

    const onLogout = () => {
      markLoggedOut()
      // A background call (e.g. a wishlist star, a cart badge) failing
      // with 401 while browsing a public page shouldn't yank an
      // anonymous visitor into /login — only bounce them if they're
      // already somewhere that needs an identity.
      if (!isPublicPath(router.state.location.pathname)) {
        void router.navigate({ to: '/login' })
      }
    }
    window.addEventListener('auth:logout', onLogout)
    return () => window.removeEventListener('auth:logout', onLogout)
  }, [setLang])

  if (!ready_ || authStatus === 'unknown') {
    return (
      <div className="flex h-screen items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-300" />
      </div>
    )
  }

  return (
    <>
      <ServerStatusBanner />
      <RouterProvider router={router} />
      <ConsentBanner measurementId={ga4MeasurementId} />
    </>
  )
}

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Bootstrap />
    </QueryClientProvider>
  )
}
